// frontend/src/lib/collectFonts.js
//
// Collect every distinct font family referenced by a design's elements so the
// editor can preload them BEFORE the canvas is shown. Without this, a design
// loaded cold (in /editor/:id or via ?template_id=) renders text in the
// browser's fallback face until the font happens to be fetched by a font
// dropdown — different glyph metrics shift text width, wrapping and alignment,
// which reads as "the saved design doesn't match the live editor".
//
// Pure + defensive: tolerates missing pages/elements, walks nested group
// children (stored either flat with parentId, or under `children`/`elements`),
// and normalizes a CSS font stack down to its primary family name.

/** Strip surrounding quotes and any CSS fallback list: `"Dancing Script", cursive` → `Dancing Script`. */
const normalizeFamily = (value) => {
  if (typeof value !== 'string') return '';
  const first = value.split(',')[0] || '';
  return first.trim().replace(/^['"]|['"]$/g, '').trim();
};

/** CSS font-weight keyword/number → numeric string ('400', '600', …). */
const normalizeWeight = (w) => {
  if (w == null || w === '') return '400';
  const s = String(w).trim().toLowerCase();
  if (s === 'normal') return '400';
  if (s === 'bold') return '700';
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= 1 && n <= 1000 ? String(n) : '400';
};

const walk = (elements, out) => {
  if (!Array.isArray(elements)) return;
  for (const el of elements) {
    if (!el || typeof el !== 'object') continue;
    const fam = normalizeFamily(el.fontFamily);
    if (fam) {
      const weight = normalizeWeight(el.fontWeight);
      const style = String(el.fontStyle || '').toLowerCase() === 'italic' ? 'italic' : 'normal';
      out.set(`${fam}|${weight}|${style}`, { family: fam, weight, style });
    }
    // Group containers may nest children under either key depending on origin.
    if (Array.isArray(el.children)) walk(el.children, out);
    if (Array.isArray(el.elements)) walk(el.elements, out);
  }
};

const collect = (pages) => {
  const out = new Map();
  if (!Array.isArray(pages)) return out;
  for (const page of pages) {
    if (!page || typeof page !== 'object') continue;
    walk(page.elements, out);
  }
  return out;
};

export function collectFontFamilies(pages) {
  return Array.from(new Set(Array.from(collect(pages).values(), (s) => s.family)));
}

/**
 * Exact (family, weight, style) faces a design uses. Loading only the family's
 * default face is NOT enough: `document.fonts.load('16px "Poppins"')` fetches
 * the 400 face, while an element with fontWeight 600 needs the 600 face — if
 * that face isn't loaded when the element first lays out, the line box is
 * computed from fallback metrics, and Safari/WebKit does not always reflow the
 * element when the real face arrives (text stays anchored at the fallback
 * baseline, visibly lower than the editor).
 */
export function collectFontSpecs(pages) {
  return Array.from(collect(pages).values());
}
